import { useState, useEffect, startTransition } from 'react';
import { useToast } from './useToast';
import { cocinaService } from '../services/cocinaService';
import { api } from '../services/api';

// Tipos para la nueva estructura de RegistroCocina
export interface RegistroCocina {
  id: number;
  codigo_registro: string;
  pedido_id: number;
  pedido_codigo: string;
  cliente_nombre: string;
  cantidad: number;
  estado: 'pendiente' | 'en_preparacion' | 'terminado';
  notas?: string;
  created_at: string;
  updated_at: string;
}

export interface GrupoReceta {
  receta_id: number;
  receta_nombre: string;
  receta_categoria: string;
  cantidad_total: number;
  total_registros: number;
  fecha_preparacion: string;
  fecha_entrega: string;
  registros: RegistroCocina[];
  ingredientes: Array<{
    ingrediente_id: number;
    nombre_ingrediente: string;
    cantidad_por_porcion: number;
    unidad: string;
    stock_disponible: number;
  }>;
  ingredientesTotales: Array<{
    ingrediente_id: number;
    nombre_ingrediente: string;
    cantidad_por_porcion: number;
    unidad: string;
    stock_disponible: number;
    cantidad_total_necesaria: number;
    suficiente: boolean;
  }>;
  ingredientesFaltantes: string[];
  tiene_ingredientes_faltantes: boolean;
  resumen_estados: {
    pendiente: number;
    en_preparacion: number;
    terminado: number;
  };
  pedidos_detalle?: Array<{
    id: number;
    codigo: string;
    cliente: string;
  }>;
  /**
   * Pareja fondo↔guarnición para tipos combinados (3,5,6,7,10).
   * Cuando la receta agregada participa de un plato compuesto y todos
   * los pedidos comparten el mismo par, el backend completa este
   * campo y la UI agrupa visualmente ambos componentes bajo el
   * nombre del plato. Si difiere entre pedidos o no aplica, `null`.
   */
  pareja: {
    receta_id: number;
    receta_nombre: string;
    rol_propio: 'fondo' | 'guarnicion';
    plato_completo: string;
  } | null;
}

export interface ResponseCocinaProduccion {
  fecha_preparacion: string;
  fecha_entrega: string;
  total_grupos_recetas: number;
  grupos_recetas: GrupoReceta[];
  pedidos_incluidos: Array<{
    id: number;
    cliente: string;
    fecha_entrega: string;
    total_items: number;
  }>;
}

export function useCocinaRegistros() {
  const [grupos, setGrupos] = useState<GrupoReceta[]>([]);
  // Vista por segmento (Fase 5, 2026-06-19). Solo se popula si el
  // backend devolvió `vistaPorSegmento` y contiene día o noche.
  const [vistaPorSegmento, setVistaPorSegmento] = useState<
    import('../services/cocinaService').VistaPorSegmento | null
  >(null);
  // Grupos de pedidos vencidos (fecha_entrega < hoy). Se cargan en
  // paralelo y se exponen en una propiedad separada para que la
  // pantalla pueda renderizarlos en un tab aparte sin mezclarlos.
  const [gruposVencidos, setGruposVencidos] = useState<GrupoReceta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fechaPreparacion, setFechaPreparacion] = useState<string>(() => {
    // Usar cocinaService para obtener fecha local consistente
    return cocinaService.obtenerFechaPreparacion(0);
  });
  const [actualizandoEstado, setActualizandoEstado] = useState<{
    [key: string]: boolean;
  }>({});

  const { success: showToast, error: showError } = useToast();

  /**
   * Fix P0 (2026-07-02) — helper compartido de actualización local.
   * Reemplaza el `await cargarDatosProduccion(...)` que hacían las
   * mutaciones (iniciar/terminar/deshacer/transicionar) al final, y
   * que congelaba el hilo principal al re-renderizar los 28 grupos del
   * tab Vencidos + los grupos del día. Ahora actualizamos SOLO el
   * registro tocado en el array de grupos + gruposVencidos, y
   * recalculamos el `resumen_estados` del grupo para que el chip refleje
   * la nueva realidad. El stock disponible de los ingredientes se
   * refresca en el próximo refresh natural (cambio de tab/fecha o botón
   * Refrescar).
   *
   * Fix terminal-state (2026-07-02, sesión siguiente): antes hacíamos
   * un cast `as unknown as` para los estados terminales
   * (`entregado_a_despacho`/`entregado_al_cliente`) y los dejábamos
   * dentro del array del grupo. Efectos colaterales: (a) el header de
   * sección seguía contando ese CR en "X pedidos"; (b) si era el
   * último no-despachado del grupo, la card quedaba fantasma en
   * Vencidos sin botones operativos hasta el próximo refresh; (c) el
   * union `RegistroCocina['estado']` se corrompía con valores fuera de
   * `pendiente|en_preparacion|terminado`.
   *
   * OPCIÓN A elegida: replicar la expulsión que hacía el refetch.
   * Cuando el estado nuevo NO es operativo (los 3 del union), removemos
   * el registro del grupo; si el grupo queda vacío, removemos el grupo.
   * Sin cast: el union se mantiene puro, `RegistroCocina['estado']` no
   * cambia. Paridad exacta con `pedidos-vencidos` del backend, que ya
   * filtra los estados terminales.
   */
  const ESTADOS_OPERATIVOS_COCINA: ReadonlyArray<RegistroCocina['estado']> = [
    'pendiente',
    'en_preparacion',
    'terminado',
  ];
  const actualizarRegistroLocalmente = (
    registroId: number,
    estadoNuevoRemoto: string
  ) => {
    const esTerminal = !ESTADOS_OPERATIVOS_COCINA.includes(
      estadoNuevoRemoto as RegistroCocina['estado']
    );
    const recalcularResumen = (registros: RegistroCocina[]) => {
      const r = { pendiente: 0, en_preparacion: 0, terminado: 0 };
      registros.forEach((reg) => {
        if (reg.estado === 'pendiente') r.pendiente++;
        else if (reg.estado === 'en_preparacion') r.en_preparacion++;
        else if (reg.estado === 'terminado') r.terminado++;
      });
      return r;
    };
    const aplicar = (arr: GrupoReceta[]): GrupoReceta[] => {
      if (esTerminal) {
        // Terminal: sacar el registro del grupo. Si el grupo queda
        // vacío, quitarlo del array. Paridad con backend
        // /pedidos-vencidos + /pedidos-produccion (que no devuelven
        // CRs terminales).
        return arr
          .map((g) => {
            const idx = g.registros.findIndex((r) => r.id === registroId);
            if (idx === -1) return g;
            const nuevosRegistros = g.registros.slice();
            nuevosRegistros.splice(idx, 1);
            const resumen = recalcularResumen(nuevosRegistros);
            return {
              ...g,
              registros: nuevosRegistros,
              total_registros: nuevosRegistros.length,
              resumen_estados: { ...g.resumen_estados, ...resumen },
            };
          })
          .filter((g) => g.registros.length > 0);
      }
      // Estado operativo: update in-place normal.
      return arr.map((g) => {
        const idx = g.registros.findIndex((r) => r.id === registroId);
        if (idx === -1) return g;
        const nuevosRegistros = g.registros.slice();
        nuevosRegistros[idx] = {
          ...nuevosRegistros[idx],
          estado: estadoNuevoRemoto as RegistroCocina['estado'],
          updated_at: new Date().toISOString(),
        };
        const resumen = recalcularResumen(nuevosRegistros);
        return {
          ...g,
          registros: nuevosRegistros,
          resumen_estados: { ...g.resumen_estados, ...resumen },
        };
      });
    };
    // Fix congelamiento P0 (2026-07-03) — dos capas de mitigación:
    //
    // 1) NO llamar ambos setState "por las dudas". Antes se llamaba
    //    setGrupos + setGruposVencidos siempre, aunque el CR viviera
    //    solo en uno. Cada setState fuerza una reconciliación del árbol
    //    de CocinaRegistros (28 grupos + subárbol de detalles), que con
    //    tab Vencidos activo puede tomar 9-60s bloqueando el hilo
    //    principal. Ahora chequeamos en cuál array vive el CR y solo
    //    actualizamos ese; el otro queda intacto (misma referencia)
    //    y React puede skipear su subárbol.
    // 2) `startTransition` para el setState que sí corresponde, marca
    //    la reconciliación como low-priority: React puede interrumpirla
    //    para procesar el toast, el for-await del masivo, y otros
    //    clicks. La UI responde inmediatamente y la reconciliación ocurre
    //    "en background".
    //
    // Regresión que quedó fuera del scope de los fixes P0/terminal
    // porque los tests previos siempre usaban 1 CR aislado en escenarios
    // menos cargados; con 28 grupos vencidos el rerender es tan pesado
    // que hasta 1 sola iteración congelaba, aunque solo hubiera 1 POST.
    const estaEnGrupos = (prev: GrupoReceta[]) =>
      prev.some((g) => g.registros.some((r) => r.id === registroId));
    startTransition(() => {
      // La lectura por `some` sobre `prev` en el updater es barata
      // (early-exit) y garantizada por React (siempre pasa el estado
      // actual). Si el CR no está en ese array, devolver `prev` no
      // dispara rerender adicional.
      setGrupos((prev) => (estaEnGrupos(prev) ? aplicar(prev) : prev));
      setGruposVencidos((prev) =>
        estaEnGrupos(prev) ? aplicar(prev) : prev
      );
    });
  };

  // Sumar N días a una fecha YYYY-MM-DD respetando zona local.
  const sumarDias = (fechaStr: string, n: number): string => {
    const [a, m, d] = fechaStr.split('-').map(Number);
    const f = new Date(a, m - 1, d);
    f.setDate(f.getDate() + n);
    const yy = f.getFullYear();
    const mm = String(f.getMonth() + 1).padStart(2, '0');
    const dd = String(f.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // Cargar datos de producción para 3 días empezando en la fecha
  // seleccionada por el chef. ANTES este código ignoraba `fecha` y
  // siempre cargaba hoy/+1/+2 — eso hacía que el selector pareciera
  // decorativo (el chef cambiaba el día pero los datos no se movían).
  // Fix 2026-06-25: las 3 llamadas usan `fecha`, `fecha+1`, `fecha+2`.
  const cargarDatosProduccion = async (fecha: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Cargando datos de producción para fecha:', fecha);

      // 3 días empezando en la fecha seleccionada (no hoy)
      const fecha0 = fecha;
      const fecha1 = sumarDias(fecha, 1);
      const fecha2 = sumarDias(fecha, 2);

      console.log('📅 Cargando datos para fechas:', {
        seleccionada: fecha0,
        siguiente: fecha1,
        siguientePlus1: fecha2,
      });

      // Hacer consultas paralelas para todas las fechas usando el servicio (con autenticación)
      const promesas = [
        cocinaService.obtenerPedidosProduccion(fecha0, true),
        cocinaService.obtenerPedidosProduccion(fecha1, true),
        cocinaService.obtenerPedidosProduccion(fecha2, true),
      ];

      const datos = await Promise.all(promesas);

      // Cargar pedidos vencidos (fecha_entrega < hoy) en paralelo.
      // El frontend pasa "hoy" en la zona del usuario; backend valida
      // con regex y cae a CURDATE() si no llega.
      const hoyLocal = cocinaService.obtenerFechaPreparacion(0);
      let dataVencidos: any = null;
      try {
        dataVencidos = await cocinaService.obtenerPedidosVencidos(hoyLocal);
      } catch (err) {
        console.warn('No se pudieron cargar pedidos vencidos:', err);
      }

      // Helper local: convierte el payload del backend (data.recetas)
      // a la estructura GrupoReceta esperada por la UI. Se reusa para
      // los datos vigentes (hoy/+1/+2) y los vencidos.
      const recetasABloques = (data: any): GrupoReceta[] => {
        if (!data?.recetas || !Array.isArray(data.recetas)) return [];
        const fechaPrep = data.fecha_preparacion || '';
        const fechaEnt = data.fecha_entrega || '';
        return data.recetas.map((receta: any) => {
          let resumenEstados;
          // Fix tanda3 H2 (2026-07-01): antes solo se consideraba
          // "válido" un resumen que tuviera >0 en `pendiente`,
          // `en_preparacion` o `terminado`. Recetas vencidas ya
          // despachadas tienen su recuento en `entregado_a_despacho`,
          // `entregado_al_cliente` o `despachado` — el check antiguo
          // decía "resumen inválido" y caía al else que las forzaba a
          // pendiente (mismo bug sistémico que la sesión #029 arregló
          // en el backend `getPedidosProduccion`; acá había una copia
          // paralela en el hook).
          const rEst = receta.resumen_estados || {};
          const tieneResumenValido =
            receta.resumen_estados &&
            ((rEst.pendiente ?? 0) > 0 ||
              (rEst.en_preparacion ?? 0) > 0 ||
              (rEst.terminado ?? 0) > 0 ||
              (rEst.entregado_a_despacho ?? 0) > 0 ||
              (rEst.entregado_al_cliente ?? 0) > 0 ||
              (rEst.despachado ?? 0) > 0);

          if (tieneResumenValido) {
            resumenEstados = receta.resumen_estados;
          } else {
            const totalPedidos = receta.pedidosRelacionados?.length || 1;
            resumenEstados = {
              pendiente: receta.estado === 'pendiente' ? totalPedidos : 0,
              en_preparacion:
                receta.estado === 'en_preparacion' ? totalPedidos : 0,
              terminado: receta.estado === 'terminado' ? totalPedidos : 0,
              entregado_a_despacho:
                receta.estado === 'entregado_a_despacho' ? totalPedidos : 0,
              entregado_al_cliente:
                receta.estado === 'entregado_al_cliente' ? totalPedidos : 0,
              despachado: receta.estado === 'despachado' ? totalPedidos : 0,
            };
            if (!receta.estado) {
              resumenEstados.pendiente = totalPedidos;
            }
          }

          return {
            receta_id: receta.id,
            receta_nombre: receta.nombre,
            receta_categoria: receta.categoria,
            cantidad_total: receta.cantidadTotal,
            total_registros: receta.pedidosRelacionados?.length || 0,
            fecha_preparacion: fechaPrep,
            fecha_entrega: fechaEnt,
            registros: receta.registros || [],
            ingredientes: [],
            ingredientesTotales: receta.ingredientesTotales || [],
            ingredientesFaltantes: receta.ingredientesFaltantes || [],
            tiene_ingredientes_faltantes:
              receta.tieneIngredientesFaltantes || false,
            resumen_estados: resumenEstados,
            pedidos_detalle: receta.pedidos_detalle || [],
            pareja: receta.pareja || null,
          };
        });
      };

      const todosLosGrupos: GrupoReceta[] = [];
      datos.forEach((data) => {
        todosLosGrupos.push(...recetasABloques(data));
      });

      const gruposVencidosCargados = dataVencidos
        ? recetasABloques(dataVencidos)
        : [];

      console.log('✅ Datos de producción cargados:', {
        totalGrupos: todosLosGrupos.length,
        totalVencidos: gruposVencidosCargados.length,
      });

      setGrupos(todosLosGrupos);
      setGruposVencidos(gruposVencidosCargados);

      // Vista por segmento del DÍA seleccionado (no de los 3 días
      // agregados). El usuario navega un día a la vez.
      const dataDelDia = datos[0];
      const vista = (dataDelDia as { vistaPorSegmento?: unknown })
        ?.vistaPorSegmento as
        | import('../services/cocinaService').VistaPorSegmento
        | undefined;
      const tieneSegmento =
        !!vista &&
        (vista.dia.recetas.length +
          vista.noche.recetas.length +
          vista.dia.insumos.length +
          vista.noche.insumos.length >
          0);
      setVistaPorSegmento(tieneSegmento ? vista : null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      console.error('❌ Error cargando datos de producción:', errorMessage);
      setError(errorMessage);
      showError('Error al cargar datos de producción', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Actualizar estado de un registro individual.
  // Fix tanda3 H3 (2026-07-01): `skipRefresh=true` evita el refetch de
  // `cargarDatosProduccion` (3 llamadas + setState en cascada) y el
  // toast por llamada. Lo usan los flujos masivos (`manejarActualizacionMasiva`,
  // `manejarDeshacerMasivo`) que hacen 1 refetch al final del batch.
  // Antes: N registros → N × (revertir + refetch + toast) → renderer
  // congelaba con vencidas.
  const actualizarEstadoRegistro = async (
    registroId: number,
    nuevoEstado: 'pendiente' | 'en_preparacion' | 'terminado',
    notas?: string,
    skipRefresh: boolean = false
  ) => {
    const registroKey = `registro-${registroId}`;

    try {
      setActualizandoEstado((prev) => ({ ...prev, [registroKey]: true }));

      console.log('🔧 Actualizando estado de registro:', {
        registroId,
        nuevoEstado,
        notas,
      });

      const response = await api.patch(`/cocina/${registroId}/estado`, {
        estado: nuevoEstado,
        notas,
      });

      const result = response.data as {
        success?: boolean;
        mensaje?: string;
        data?: { estado?: string };
      };
      console.log('✅ Estado de registro actualizado:', result);

      // Fix P0 (2026-07-02): antes hacíamos `await cargarDatosProduccion`
      // aquí (path individual sin skipRefresh) que con tab Vencidos
      // activo congelaba el hilo principal por el rerender pesado (mismo
      // patrón que Deshacer). El backend responde con `data.estado`; si
      // no llega, caemos al argumento `nuevoEstado`. skipRefresh se
      // mantiene por compat con `manejarActualizacionMasiva` — ahí lo
      // usan para saltar el toast individual, y el update in-place se
      // aplica igual.
      const estadoNuevoRemoto = result.data?.estado ?? nuevoEstado;
      actualizarRegistroLocalmente(registroId, estadoNuevoRemoto);
      if (!skipRefresh) {
        const mensajeBase =
          result.mensaje || 'Estado actualizado correctamente';
        const mensajeExtra =
          nuevoEstado === 'en_preparacion'
            ? 'Inventario actualizado automáticamente'
            : '';
        showToast(mensajeBase, mensajeExtra);
      }
    } catch (err) {
      // Fix tanda3 stock (2026-07-01): el backend responde 409 cuando
      // no hay stock suficiente con `data.detalles.ingredientes_faltantes`.
      // Antes se mostraba sólo "Error al actualizar estado" y el chef
      // no entendía por qué; ahora armamos un mensaje explícito con
      // el detalle por ingrediente para que sepa qué le falta.
      type Faltante = {
        nombre: string;
        cantidad_necesaria: number;
        stock_disponible: number;
        unidad?: string;
      };
      const anyErr = err as {
        response?: {
          status?: number;
          data?: {
            error?: string;
            detalles?: {
              receta_nombre?: string;
              ingredientes_faltantes?: Faltante[];
            };
          };
        };
      };
      const status = anyErr.response?.status;
      const detalles = anyErr.response?.data?.detalles;
      const faltantes = detalles?.ingredientes_faltantes;

      if (status === 409 && faltantes && faltantes.length > 0) {
        const receta = detalles?.receta_nombre
          ? ` (${detalles.receta_nombre})`
          : '';
        const lineas = faltantes
          .map(
            (f) =>
              `• ${f.nombre}: hay ${f.stock_disponible} ${f.unidad ?? ''}, se requieren ${f.cantidad_necesaria} ${f.unidad ?? ''}`
          )
          .join('\n');
        showError(
          `Stock insuficiente${receta}`,
          lineas
        );
      } else {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        console.error(
          '❌ Error actualizando estado de registro:',
          errorMessage
        );
        showError(
          anyErr.response?.data?.error || 'Error al actualizar estado'
        );
      }
      // Re-lanzar para que los callers masivos también sepan que falló
      // y no queden en "Guardando…" (aprender de H6 Mermas).
      throw err;
    } finally {
      setActualizandoEstado((prev) => ({ ...prev, [registroKey]: false }));
    }
  };

  /**
   * Revertir el estado de un registro individual.
   * - en_preparacion → pendiente (devuelve stock en backend; transaccional).
   * - terminado → en_preparacion (solo cambia estado).
   * - despachado → 409 (terminal).
   * Backend: POST /cocina/:id/revertir (controllers/cocina.ts).
   */
  const revertirEstadoRegistro = async (
    registroId: number,
    skipRefresh: boolean = false
  ): Promise<void> => {
    const key = `revertir-${registroId}`;
    try {
      setActualizandoEstado((prev) => ({ ...prev, [key]: true }));
      const response = await api.post(`/cocina/${registroId}/revertir`);
      const result = response.data as {
        success: boolean;
        estado_anterior?: string;
        estado_nuevo?: string;
        stock_revertido?: boolean;
        detalle_reverso?: { movimientosRevertidos: number };
      };
      // Fix P0 (2026-07-02): update in-place vía helper compartido
      // `actualizarRegistroLocalmente`. Antes hacíamos
      // `cargarDatosProduccion` aquí y congelaba el hilo principal
      // 30-60s al re-renderizar 28 grupos vencidos.
      const estadoNuevoRemoto = result.estado_nuevo ?? 'pendiente';
      actualizarRegistroLocalmente(registroId, estadoNuevoRemoto);
      if (!skipRefresh) {
        const extra = result.stock_revertido
          ? `Stock revertido (${result.detalle_reverso?.movimientosRevertidos ?? 0} movimientos invertidos)`
          : 'Estado revertido (no toca stock)';
        showToast(`Volvió a ${result.estado_nuevo}`, extra);
      }
    } catch (err) {
      const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
      const msg =
        anyErr.response?.data?.error ||
        anyErr.message ||
        'No se pudo revertir';
      showError(msg);
      throw err;
    } finally {
      setActualizandoEstado((prev) => ({ ...prev, [key]: false }));
    }
  };

  /**
   * Transición de estado FSM con comentario opcional.
   * Útil para: terminado → entregado_a_despacho (en Cocina), y
   * entregado_a_despacho → entregado_al_cliente (en Despacho).
   * Backend: POST /cocina/:id/transicion {estado_nuevo, comentario?}.
   */
  const transicionarEstadoRegistro = async (
    registroId: number,
    estadoNuevo:
      | 'pendiente'
      | 'en_preparacion'
      | 'terminado'
      | 'entregado_a_despacho'
      | 'entregado_al_cliente',
    comentario?: string
  ): Promise<void> => {
    const key = `transicion-${registroId}`;
    try {
      setActualizandoEstado((prev) => ({ ...prev, [key]: true }));
      const response = await api.post(`/cocina/${registroId}/transicion`, {
        estado_nuevo: estadoNuevo,
        comentario: comentario && comentario.trim() ? comentario.trim() : null,
      });
      const result = response.data as {
        success: boolean;
        estado_anterior?: string;
        estado_nuevo?: string;
      };
      // Fix P0 (2026-07-02): antes `await cargarDatosProduccion` aquí
      // congelaba el hilo principal cuando `confirmarEntregarDespacho`
      // hacia `Promise.allSettled` sobre N registros (N × refetch en
      // paralelo). Ahora update in-place del registro tocado; el estado
      // nuevo lo leemos de la respuesta del backend, con `estadoNuevo`
      // como fallback si no llega.
      const estadoNuevoRemoto = result.estado_nuevo ?? estadoNuevo;
      actualizarRegistroLocalmente(registroId, estadoNuevoRemoto);
      showToast(
        `Estado actualizado: ${result.estado_nuevo}`,
        comentario ? 'Comentario guardado en historial' : ''
      );
    } catch (err) {
      const anyErr = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      const msg =
        anyErr.response?.data?.error ||
        anyErr.message ||
        'No se pudo cambiar el estado';
      showError(msg);
      throw err;
    } finally {
      setActualizandoEstado((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Actualizar estado masivo para una receta
  const actualizarEstadoMasivo = async (
    recetaId: number,
    nuevoEstado: 'pendiente' | 'en_preparacion' | 'terminado',
    registrosSeleccionados?: number[],
    grupo?: GrupoReceta
  ) => {
    const masivo_key = `masivo-${recetaId}`;

    try {
      setActualizandoEstado((prev) => ({ ...prev, [masivo_key]: true }));

      // Usar la fecha del grupo si está disponible, sino usar la fecha del estado
      const fechaPreparacionGrupo =
        grupo?.fecha_preparacion || fechaPreparacion;

      console.log('🔧 Actualizando estado masivo:', {
        recetaId,
        nuevoEstado,
        fechaPreparacion: fechaPreparacionGrupo,
        registrosSeleccionados,
      });

      // Si va a iniciar preparación, validar ingredientes primero
      if (nuevoEstado === 'en_preparacion' && grupo) {
        const tieneIngredientesInsuficientes =
          grupo.ingredientesTotales &&
          grupo.ingredientesTotales.some((ing) => !ing.suficiente);

        if (tieneIngredientesInsuficientes) {
          // Lanzar error con información del grupo para que el componente lo maneje
          const error = new Error('INGREDIENTES_INSUFICIENTES') as any;
          error.grupo = grupo;
          throw error;
        }
      }

      const response = await api.patch('/cocina/estado-masivo', {
        receta_id: recetaId,
        fecha_preparacion: fechaPreparacionGrupo,
        estado: nuevoEstado,
        incluir_registros: registrosSeleccionados,
      });

      const result = response.data;
      console.log('✅ Estado masivo actualizado:', result);

      // Recargar datos (incluye stock actualizado tras descuento de inventario)
      console.log('🔄 Recargando datos de cocina tras actualización masiva...');
      await cargarDatosProduccion(fechaPreparacion);

      const mensajeBase =
        result.mensaje || 'Estados actualizados correctamente';
      const mensajeExtra =
        nuevoEstado === 'en_preparacion'
          ? 'Inventario actualizado automáticamente para todos los registros'
          : '';

      showToast(mensajeBase, mensajeExtra);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      console.error('❌ Error actualizando estado masivo:', errorMessage);

      // Si es error de ingredientes insuficientes, re-lanzar sin mostrar toast
      // para que el componente lo maneje con el modal
      if (errorMessage === 'INGREDIENTES_INSUFICIENTES') {
        throw err; // Re-lanzar el error para que lo capture el componente
      }

      // Para otros errores, mostrar toast
      showError(`Error al actualizar estados: ${errorMessage}`);
    } finally {
      setActualizandoEstado((prev) => ({ ...prev, [masivo_key]: false }));
    }
  };

  // Funciones auxiliares para UI
  const obtenerTextoBotonRegistro = (estado: string): string => {
    switch (estado) {
      case 'pendiente':
        return 'Iniciar preparación';
      case 'en_preparacion':
        return 'Marcar como terminado';
      case 'terminado':
        return 'Terminado ✓';
      default:
        return 'Estado desconocido';
    }
  };

  const obtenerEstiloBotonRegistro = (estado: string): string => {
    const baseClasses =
      'px-3 py-1 rounded text-sm font-medium transition-colors';

    switch (estado) {
      case 'pendiente':
        return `${baseClasses} bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-500 text-white`;
      case 'en_preparacion':
        return `${baseClasses} bg-green-500 dark:bg-green-600 hover:bg-green-600 dark:hover:bg-green-500 text-white`;
      case 'terminado':
        return `${baseClasses} bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed`;
      default:
        return `${baseClasses} bg-gray-500 dark:bg-gray-600 text-white`;
    }
  };

  const obtenerProximoEstado = (
    estadoActual: string
  ): 'pendiente' | 'en_preparacion' | 'terminado' | null => {
    switch (estadoActual) {
      case 'pendiente':
        return 'en_preparacion';
      case 'en_preparacion':
        return 'terminado';
      case 'terminado':
        return null; // No hay próximo estado
      default:
        return null;
    }
  };

  const obtenerIconoProximoEstado = (estadoActual: string): string => {
    switch (estadoActual) {
      case 'pendiente':
        return '🔄'; // Icono para iniciar preparación
      case 'en_preparacion':
        return '✅'; // Icono para marcar como terminado
      case 'terminado':
        return '✓'; // Icono de terminado
      default:
        return '❓'; // Icono para estado desconocido
    }
  };

  const cambiarFechaPreparacion = (fecha: string) => {
    setFechaPreparacion(fecha);
  };

  // Cargar datos cuando cambia la fecha
  useEffect(() => {
    cargarDatosProduccion(fechaPreparacion);
  }, [fechaPreparacion]);

  return {
    // Estado
    grupos,
    gruposVencidos,
    vistaPorSegmento,
    loading,
    error,
    fechaPreparacion,
    actualizandoEstado,

    // Acciones
    cargarDatosProduccion,
    actualizarEstadoRegistro,
    revertirEstadoRegistro,
    transicionarEstadoRegistro,
    actualizarEstadoMasivo,
    cambiarFechaPreparacion,

    // Utilidades UI
    obtenerTextoBotonRegistro,
    obtenerEstiloBotonRegistro,
    obtenerProximoEstado,
    obtenerIconoProximoEstado,
  };
}
